import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row, Card, Button, Form, Input, Select, Steps, Checkbox, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { gql, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { getOrganisationDetails } from "../../features/organisations/organisationsSlice";

const Emails = gql`
  query getEmails {
    organisationEmails {
      data {
        id
        attributes {
          email
          name
        }
      }
    }
  }
`;

const MemberNetworks = gql`
  query getMemberNetworks($id: String!) {
      memberNetworks(filters: { organisation: { orgId: { eq: $id } } }, pagination: { limit: 1000 }) {
          data {
              id
              attributes {
                  name
              }
          }
      }
  }
`;

export default function Register() {
  const { data } = useQuery(Emails);
  const { organisation } = useSelector((state) => state.organisation);
  const { data: memberNetworkData } = useQuery(MemberNetworks, {
    variables: {
      id: organisation?.attributes?.orgId,
    },
  });
  const { isLoading, auth } = useAuth();
  const navigate = useNavigate();
  const [isEmail, setIsEmail] = useState(true);
  const [showRegistration, setShowRegistration] = useState(false);
  const [step, setStep] = useState(0);
  const [registerFields, setRegisterFields] = useState({});
  const [acceptedLinks, setAcceptedLinks] = useState([]);

  useEffect(() => {
    if (organisation && !organisation.attributes.LMS.registrationPassword?.length) {
      setShowRegistration(true);
    }
  }, [organisation]);

  const onRegister = async (values) => {
    try {
      // if honeypot field is filled, do not proceed
      if (values.email) return;

      if (step === 0) {
        setRegisterFields({ ...registerFields, ...values });
        setStep(1);
        return;
      }

      let id;
      let slug;
      let orgEmail;

      if (!registerFields.organisation) {
        const org = await getOrganisationDetails();
        id = org.id;
        slug = org.slug;
        orgEmail = org.email;
      } else {
        const org = organisation.attributes.allowOtherTenants.data.find((tenant) => tenant.attributes.slug === registerFields.organisation);
        id = org.id;
        slug = org.attributes.slug;
        orgEmail = org.attributes.email;
      }

      const form = { ...registerFields, ...values };
      const phone = form?.phone?.replace(/[^0-9]/g, "");
      const user_identifier = form.identifier;
      let email = form.identifier;
      if (!isEmail) {
        const [orgName, domain] = orgEmail.split("@");
        email = `${orgName}+${form.identifier}@${domain}`;
      }
      const identifier = isEmail ? form.identifier : `${slug}-${form.identifier}`;
      console.log("Registering with", { ...form, phone, email, user_identifier, orgId: id, identifier });
      const response = await auth.register({ ...form, phone, email, user_identifier, orgId: id, identifier });
      console.log("Registration response", response);
      navigate("/");
      window.location.reload();
    } catch (error) {
      console.error("Registration failed:", error);
      if (error.message === "Email or Username are already taken") {
        toast("Email or Username are already taken", { type: "error" });
      } else {
        toast("Registration failed", { type: "error" });
      }
    }
  };

  const getItems = () => {
    const allowedTenants = organisation?.attributes?.allowOtherTenants?.data;
    if (!allowedTenants) return [];
    const items = allowedTenants.map((tenant) => {
      return {
        id: tenant.id,
        name: tenant.attributes.name,
        value: tenant.attributes.slug,
      };
    });
    items.unshift({
      id: organisation.id,
      name: organisation.attributes.name,
      value: organisation.attributes.slug,
    });
    return items;
  };
  const getNetworks = () => {
    return memberNetworkData?.memberNetworks?.data?.map((network) => {
      return {
        id: network.id,
        name: network.attributes.name,
        value: network.id,
      };
    });
  };
  const handleUsernameChange = (e) => {
    const username = e.target.value;
    const emailRegex = /\S+@\S+\.\S+/;
    setIsEmail(emailRegex.test(username));
  };

  const unlockRegistration = (values) => {
    if (organisation.attributes.LMS.registrationPassword === values.password) {
      setShowRegistration(true);
    } else {
      toast("Invalid password", { type: "error" });
    }
  };

  const handleCheckboxChange = (link) => {
    setAcceptedLinks((prev) =>
      prev.includes(link)
        ? prev.filter((item) => item !== link)
        : [...prev, link]
    );
  };

  if (!organisation) return <div className="AlertInfo Loading">Loading...</div>;

  const externalRegistrationAllowed = !!organisation.attributes.allowExternalRegistrations;
  const showMemberNetworks = !!organisation.attributes.showMemberNetworks;
  const separateDANumber = !!organisation.attributes.LMS?.seperate_DA_number_in_registration;
  const requiredLinks = organisation.attributes.LMS?.required_links ?? [];
  const enableRequiredLinks = !!organisation.attributes.LMS?.enable_required_links && requiredLinks?.length > 0;

  const allLinksAccepted = enableRequiredLinks
    ? requiredLinks.every((link) => acceptedLinks.includes(link))
    : true;

  return (
    <div className="Register">
      <div className="AppContainer">
        <Row justify="center" align="middle">
          <Col xs={24} md={14} lg={10}>
            <Card>
              <h1 className="SectionTitle">Register</h1>
              {showRegistration ? (
                <div>
                  <Steps
                    className="PageSteps"
                    current={step}
                    labelPlacement="vertical"
                    items={[
                      {
                        title: "Member Details",
                      },
                      {
                        title: "Login Details",
                      },
                    ]}
                  />
                  <Form
                    name="normal_login"
                    className="login-form"
                    initialValues={{
                      remember: true,
                    }}
                    onFinish={onRegister}
                    size="large"
                    layout="vertical"
                  >
                    {step === 0 ? (
                      <>
                        {/* Honeypot field to handle bots */}
                        <Form.Item hidden={true} name="email" label="Email">
                          <Input placeholder="Do not fill this if you are human" />
                        </Form.Item>
                        {showMemberNetworks && (
                          <Form.Item
                            name="MemberNetwork"
                            label="Member Network"
                            rules={[
                              {
                                required: true,
                                message: "Please input your Member Network!",
                              },
                            ]}
                          >
                            <Select placeholder="Select Member Network">
                              {getNetworks()?.map((item) => (
                                <Select.Option key={item.value} value={item.value}>
                                  {item.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )}
                        <Form.Item
                          name="firstname"
                          label="First Name"
                          rules={[
                            {
                              required: true,
                              message: "Please input your First Name!",
                            },
                          ]}
                        >
                          <Input placeholder="First name" />
                        </Form.Item>
                        <Form.Item
                          name="lastname"
                          label="Last Name"
                          rules={[
                            {
                              required: true,
                              message: "Please input your Last Name!",
                            },
                          ]}
                        >
                          <Input placeholder="Last name" />
                        </Form.Item>
                        {externalRegistrationAllowed && (
                          <Form.Item
                            name="organisation"
                            label="Organisation"
                            rules={[
                              {
                                required: true,
                                message: "Please input your Organisation!",
                              },
                            ]}
                          >
                            <Select placeholder="Select Organisation">
                              {getItems()?.map((item) => (
                                <Select.Option key={item.value} value={item.value}>
                                  {item.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )}
                      </>
                    ) : (
                      <>
                        <Form.Item
                          name="phone"
                          label="Phone Number"
                          rules={[
                            {
                              required: isEmail ? false : true,
                              message: "Please input your Phone Number! (04XXXXXXXX)'",
                              pattern: /^04\d{8}$/,
                            },
                          ]}
                        >
                          <Input placeholder="Phone number" />
                        </Form.Item>
                        {separateDANumber ? (
                          <>
                            <Form.Item
                              name="identifier"
                              label="Username/Email"
                              rules={[
                                {
                                  required: true,
                                  message: "Please input your Username/DA Number or Email!",
                                },
                              ]}
                            >
                              <Input placeholder="Username/Email" onChange={handleUsernameChange} />
                            </Form.Item>
                            <Form.Item
                              name="DANumber"
                              label="Taxi Driver Number"
                              rules={[
                                {
                                  required: true,
                                  message: "Please input your Driver Number!",
                                },
                              ]}
                            >
                              <Input placeholder="Taxi Driver Number" autoComplete={false}/>
                            </Form.Item>
                          </>
                        ) : (
                          <>
                            <Form.Item
                              name="identifier"
                              label="Username/DA Number/Email"
                              rules={[
                                {
                                  required: true,
                                  message: "Please input your Username/DA Number or Email!",
                                },
                              ]}
                            >
                              <Input placeholder="Username/DA Number/Email" onChange={handleUsernameChange} />
                            </Form.Item>
                          </>
                        )}

                        <Form.Item
                          name="password"
                          label="Password"
                          rules={[
                            {
                              required: true,
                              message: "Please input your Password!",
                              pattern: new RegExp("^.{4,}$"),
                            },
                          ]}
                        >
                          <Input type="password" placeholder="Password" />
                        </Form.Item>

                        {enableRequiredLinks &&
                          requiredLinks.map((link, index) => (
                            <Form.Item key={index} valuePropName="checked" style={{ margin: 0 }}>
                              <Checkbox onChange={() => handleCheckboxChange(link)} style={{margin: 0}}>
                                I accept the <a href={link.url} target="_blank" rel="noopener noreferrer">{link.name}</a>
                              </Checkbox>
                            </Form.Item>
                          ))}
                      </>
                    )}

                    <Form.Item>
                      <Tooltip title={!allLinksAccepted ? "Please accept all required links to proceed" : ""}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          className="login-form-button"
                          loading={isLoading}
                          disabled={step === 1 && !allLinksAccepted}
                        >
                          {step == 0 ? "Next" : "Register"}
                        </Button>
                      </Tooltip>
                    </Form.Item>
                    <Form.Item>
                      <div style={{ textAlign: "center", width: "100%" }}>
                        <a href="/login">Login Now!</a>
                      </div>
                    </Form.Item>
                  </Form>
                </div>
              ) : (
                <Form name="unlock_registration" className="login-form" onFinish={unlockRegistration} size="large" layout="vertical">
                  <Form.Item name="password" label="Enter Password to unlock registration">
                    <Input type="password" placeholder="Password" />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" className="login-form-button" loading={isLoading}>
                      Unlock
                    </Button>
                  </Form.Item>
                </Form>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
